// What the screens and cloud's api share about publishing a document by link.
// The server is cloud's; the screen (ui-core's publishing/) is put in by
// cloud's web, and by the offline edition while personal data is read from
// the cloud (its core sends these requests there).
//
// REST (the person's own personal documents, and the workspace documents they
// may write where the workspace allows publishing; `:id` is the document's id
// or key; a device token is refused with 403):
// - GET    /api/me/pages/:id/publication          → PublicationStateDTO
// - POST   /api/me/pages/:id/publication          → PublicationDTO (201 made, 200 already live)
// - DELETE /api/me/pages/:id/publication          → 204 (also when nothing was live)
// - GET    /api/me/pages/:id/publication/preview  → PublicationPreviewDTO
// - GET    /api/publication-previews/:token       → the preview's page (html;
//          no session: the address from the preview is signed, 404 once it
//          ran out or the person may no longer publish the document)
// - GET    /api/me/pages/:id/publication/datasets → PublicationDatasetsDTO
// - POST   /api/me/pages/:id/publication/datasets → PublicationDatasetsDTO
//          body { add: string[] } (409 `not_published`; 403
//          `dataset_not_publishable` with `details.datasetId` and `state`)
//
// POST …/publication takes `{ datasets: string[] }`: the datasets the person
// was shown in the preview (only those the document reads and they may
// publish go out with it).
// - GET    /api/me/publications                   → { publications: MyPublicationDTO[] }
//
// Without a public store (the deployment has none) POST and preview answer
// 503 `publish_unavailable`; GET says `available: false`. Stopping always works.
// Other errors: 404 `page_not_found` (not visible, or not the person's own),
// 409 `archived`, 400 `unsupported_kind`.

export interface PublicationDTO {
  id: string;
  /** The unguessable part of the address. */
  publicId: string;
  /** Where anyone can read it. */
  url: string;
  createdAt: string;
  createdByUserId: string | null;
  /** When the copy now online was put there; null until the first one is up. */
  builtAt: string | null;
  /** The last try to put it failed (it is tried again until it works). */
  failing: boolean;
  /** The datasets published with it. */
  datasetIds: string[];
}

/**
 * Marks the messages between a publish dialog's preview (the published page's
 * script) and the dialog around it: `{ type: "dataWant", id, wants }` from the
 * preview, `{ type: "data", id, results: { [key]: UiDataState } }` back
 * (results only, never rows).
 */
export const PUB_PREVIEW_SOURCE = "trameria-pub-preview";

/** A dataset a document's UI reads, as the publish dialog shows it. */
export interface PublicationDatasetDTO {
  id: string;
  title: string;
  /** The space's name; null for a personal dataset. */
  space: string | null;
  /** Its workspace's slug (its screen is `/<slug>/documents/datasets/<id>`); null for a personal one. */
  workspace: string | null;
  rows: number;
  /** Its fields' names (from its schema, else from its rows). */
  fields: string[];
  /**
   * publish: it can go out with the document. not_writable: the person may
   * not write it. not_allowed: its workspace does not allow publishing.
   */
  state: "publish" | "not_writable" | "not_allowed";
}

export interface PublicationDatasetsDTO {
  /** Published with the document now. */
  published: PublicationDatasetDTO[];
  /** Read by the document but not published. */
  unpublished: PublicationDatasetDTO[];
  /** Read by the document but not seen by the person (no name shown). */
  hiddenCount: number;
}

export interface PublicationStateDTO {
  /** Whether this server can publish at all (a public store is set up). */
  available: boolean;
  /** The live publication, or null. */
  publication: PublicationDTO | null;
}

export interface PublicationPreviewDTO {
  /**
   * The page as it would be published, as a page of its own to frame (images
   * through signed URLs). Its address needs no session and lasts at least 10
   * minutes; asked again with nothing changed, it is the same address. The
   * page brings its own policy (no origin, scripts and images from pub and
   * the file store only), so it draws the same in any screen's frame (the
   * web's, the desktop app's, a phone app's).
   */
  url: string;
  /**
   * References that will show only their kind (markdown), or links that will
   * be taken off (html, slide), because what they point at is not published.
   */
  plainRefCount: number;
  /** The datasets its UIs read that the person sees (those with state publish go out with it). */
  datasets: PublicationDatasetDTO[];
  /** Read datasets the person does not see. */
  hiddenDatasetCount: number;
}

export interface MyPublicationDTO extends PublicationDTO {
  pageId: string;
  pageKey: string;
  title: string;
  kind: "markdown" | "html" | "slide";
}

/** The kinds of document that can be published. */
export const PUBLISHABLE_KINDS = ["markdown", "html", "slide"] as const;
